import Container from '@brands/Components/Container/Container';
import LinearProgressDisplay from '@brands/Components/ProgressDisplay/ProgressDisplay';
import BrandLogo from '@brands/Components/BrandLogo/BrandLogo';
import closeIcon from '@brands/Components/PatientIntakeNavigation/Icons/patientIntakeNavigationClose.svg';
import intakeLogo from '@brands/Components/PatientIntakeNavigation/Icons/patientIntakeNavigationLogo.svg';
import AbandonIntakeProcessModal from '@brands/Components/PatientIntakeNavigation/Modal/AbandonIntakeProcessModal';
import NotFoundPage from '@brands/Dashboard/NotFoundPage/NotFoundPage';
import { useAppDispatch, useAppSelector } from '@brands/hooks/useReduxHook';
import { selectAuth } from '@brands/store/selectors/auth';
import { selectClientBranding } from '@brands/store/selectors/clientBranding';
import { selectPageState } from '@brands/store/selectors/pageState';
import { setDependentId, setIsFromSelectPatient } from '@brands/store/slices/pageStateSlice';
import { setStaffIntakeInProgress } from '@brands/store/slices/staffCaseCreationSlice';
import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { UserRoleName } from '../../../services/identity/types/UserProfile';
import AdminMyProfile from '../Admin/MyProfile';
import CareAdvisorMyProfile from '../CareAdvisor/MyProfile';
import CollectorMyProfile from '../Collector/MyProfile';
import OrganizationAdmin from '../OrganizationAdmin/MyProfile';
import PatientMyProfile from '../Patient/Sections/ReviewMyProfile/MyProfile';
import ProviderMyProfile from '../Provider/MyProfile';

import styles from './reviewProfile.module.scss';

const COLLECTOR_PROFILE_PROGRESS = 25;

function getCollectorHome(roleName: string): string {
  return `/${roleName.charAt(0).toUpperCase()}${roleName.slice(1)}/dashboard`;
}

const ReviewProfile = (): JSX.Element | null => {
  const params = useParams();
  const roleName = ((params.targetRole ?? params.role) as string).toLowerCase();
  let component: JSX.Element;

  const user = useAppSelector(selectAuth);
  const { isFromSelectPatient } = useAppSelector(selectPageState);
  const clientBranding = useAppSelector(selectClientBranding);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [displayAbandonIntakeProcessModal, setDisplayAbandonIntakeProcessModal] = useState(false);

  if (!user.userInfo) {
    window.location.replace('/');
    return null;
  }

  const isStaffCaseCreation =
    (user.userInfo.role.name === UserRoleName.Collector &&
      location.pathname.startsWith('/collector/case-creation/')) ||
    (user.userInfo.role.name === UserRoleName.CareAdvisor &&
      location.pathname.startsWith('/careadvisor/case-creation/')) ||
    ((user.userInfo.role.name === UserRoleName.Admin ||
      user.userInfo.role.name === UserRoleName.SuperAdmin ||
      user.userInfo.role.name === UserRoleName.OrganizationAdmin) &&
      isFromSelectPatient &&
      // The router lowercases role-prefixed URLs (`/SuperAdmin/...` becomes `/superadmin/...`).
      location.pathname.toLowerCase().startsWith(`/${user.userInfo.role.name.toLowerCase()}/review-profile/`) &&
      (roleName === UserRoleName.Patient.toLowerCase() || roleName === UserRoleName.Dependent.toLowerCase()));

  switch (roleName) {
    case UserRoleName.CareAdvisor.toLowerCase():
      component = <CareAdvisorMyProfile reviewProfile />;
      break;
    case UserRoleName.Provider.toLowerCase():
      component = <ProviderMyProfile reviewProfile />;
      break;
    case UserRoleName.Patient.toLowerCase():
    case UserRoleName.Dependent.toLowerCase():
      component = <PatientMyProfile key={params.userId} reviewProfile />;
      break;
    case UserRoleName.Admin.toLowerCase():
    case UserRoleName.SuperAdmin.toLowerCase():
      component = <AdminMyProfile reviewProfile />;
      break;
    case UserRoleName.OrganizationAdmin.toLowerCase():
      component = <OrganizationAdmin reviewProfile />;
      break;
    case UserRoleName.Collector.toLowerCase():
      component = <CollectorMyProfile reviewProfile />;
      break;
    default:
      component = <NotFoundPage />;
      break;
  }

  if (isStaffCaseCreation) {
    const handleClose = (): void => {
      dispatch(setIsFromSelectPatient(false));
      dispatch(setDependentId(null));
      dispatch(setStaffIntakeInProgress(false));
      if (
        user.userInfo.role.name === UserRoleName.Admin ||
        user.userInfo.role.name === UserRoleName.SuperAdmin ||
        user.userInfo.role.name === UserRoleName.OrganizationAdmin
      ) {
        navigate(`/${user.userInfo.role.name}/dashboard`);
        return;
      }
      navigate(getCollectorHome(user.userInfo.role.name));
    };
    return (
      <div className={styles.collectorCcFullPage}>
        <header className={styles.reviewProfileIntakeHeader}>
          <div className={styles.reviewProfileIntakeHeaderTop}>
            <BrandLogo
              customLogoUrl={clientBranding?.logo_url}
              fallbackLogo={intakeLogo}
              className={styles.reviewProfileIntakeLogo}
            />
            <button
              type="button"
              onClick={() => setDisplayAbandonIntakeProcessModal(true)}
              className={styles.reviewProfileIntakeCloseIcon}
              aria-label="Close and exit"
            >
              <img src={closeIcon} alt="" />
            </button>
          </div>
          <LinearProgressDisplay progress={COLLECTOR_PROFILE_PROGRESS} />
        </header>
        <main className={styles.collectorCcMain}>{component}</main>
        {displayAbandonIntakeProcessModal && (
          <AbandonIntakeProcessModal
            setDisplayAbandonIntakeProcessModal={setDisplayAbandonIntakeProcessModal}
            onConfirmExit={handleClose}
          />
        )}
      </div>
    );
  }

  return (
    <Container
      patientDashboard
      isVisibleOnMobile={false}
      childClassName={styles.reviewProfileShell}
      containerStyle={{ overflowY: 'hidden' }}
      childrenStyle={{
        alignItems: 'flex-start',
        width: '100%',
      }}
    >
      <div className={styles.layoutBody}>
        <main className={styles.layoutContent}>{component}</main>
      </div>
    </Container>
  );
};
export default ReviewProfile;
